/**
 * The one email shape check every Privacy Bee surface runs.
 */
/**
 * `local@domain.tld`: no whitespace anywhere, no invisible formatting character
 * anywhere, exactly one `@`, at least one dot in the domain.
 *
 * Deliberately structural rather than RFC 5322. The only question a form can
 * usefully answer before submitting is "could this reach a mailbox at all" —
 * a stricter pattern rejects addresses that genuinely deliver (quoted locals,
 * long new TLDs) and the authoritative answer is the verification mail the API
 * sends either way. Exported so nothing has to retype it.
 *
 * `\p{Cf}` is excluded alongside whitespace because a zero-width space, a word
 * joiner or a soft hyphen is invisible on screen: the address looks exactly
 * like the one the customer meant, reaches no mailbox, and compares unequal to
 * the same address typed anywhere else. Rejecting it is the visible failure —
 * stripping it would send mail to an address nobody typed.
 *
 * No `g` flag: a shared regex with `lastIndex` state gives alternating answers
 * to `.test()`.
 */
export const EMAIL_PATTERN = /^[^\s@\p{Cf}]+@[^\s@\p{Cf}]+\.[^\s@\p{Cf}]+$/u;
/**
 * The address with the whitespace around it gone: the one trim, run where a
 * value arrives — a field, a `?email=` parameter, an imported row — and before
 * anything validates, stores or sends it.
 *
 * A pasted address carries a leading or trailing space, tab or newline
 * constantly, out of a spreadsheet cell, a mail client or a PDF, and it is
 * still the address the customer means. The trim lives here rather than in each
 * form because a package that leaves it to its callers is how one app comes to
 * accept what its neighbour rejects, which is the divergence this module exists
 * to end.
 *
 * Case is left alone. The local part of an address is case-sensitive by
 * specification, and lowercasing one folds two mailboxes into one identity.
 */
export function normalizeEmail(value) {
    if (!value)
        return "";
    return value.trim();
}
/**
 * Whether a value is shaped like a deliverable email address.
 *
 * It judges exactly the string it is handed. Surrounding whitespace is a reason
 * to call `normalizeEmail` first, not something this quietly forgives: a
 * caller's next move is to send the value it validated, and an address that
 * passes here with a space still on it reaches an identity lookup that compares
 * bytes — one customer, two accounts. Normalize at the boundary, then ask.
 *
 * Nullish and empty input is `false` rather than a throw, so a form can pipe an
 * optional field straight through without narrowing it first.
 */
export function isValidEmail(value) {
    if (!value)
        return false;
    return EMAIL_PATTERN.test(value);
}
